import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataService, ExerciseSettingsMode, ExerciseSettingsModeType } from '../../../services/data.service';
import { Subscription } from 'rxjs';
import { SlotModel } from '../../../models/slot.model';
import { AcModel } from '../../../models/ac.model';
import { ExerciseModel } from '../../../models/exercise.model';
import { ApiService } from '../../../services/api.service';
import { AcStatusSettingsExerciseType } from '../../../models/acstatus/ac-status.type';
import { UiService } from '../../../services/ui.service';
import { TranslocoService } from '@ngneat/transloco';
import { SelectItem } from 'primeng/api';
import { ExerciseSettingsGroupsComponent } from '../exercise-settings-groups/exercise-settings-groups.component';
import { ExerciseSettingsSlotsComponent } from '../exercise-settings-slots/exercise-settings-slots.component';

@Component({
  selector: 'app-exercise-settings-dialog',
  templateUrl: './exercise-settings-dialog.component.html',
  styleUrls: ['./exercise-settings-dialog.component.scss']
})
export class ExerciseSettingsDialogComponent implements OnInit, OnDestroy {

  @ViewChild('groups') groups: ExerciseSettingsGroupsComponent;
  @ViewChild('slots') slots: ExerciseSettingsSlotsComponent;

  private acSubscription: Subscription = null;
  private settingsSubscription: Subscription = null;

  public ac: AcModel = null;
  public mode: ExerciseSettingsMode;
  public slot: SlotModel;
  public exercise: ExerciseModel;
  public visible = false;
  public submitting = false;
  public has_matrix = false;
  public automatic_start = false;

  public tabs: Array<SelectItem> = [];
  public activeTab: SelectItem = null;

  constructor(
    private data: DataService,
    private api: ApiService,
    private ui: UiService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.acSubscription = this.data.ac$.subscribe((ac: AcModel) => {
      this.ac = ac;
      this.initTabs();
    });
    this.settingsSubscription = this.data.exerciseSettings$.subscribe((settings: ExerciseSettingsModeType) => {
      this.mode = settings?.mode || null;
      this.exercise = this.ac?.exercises.find(exercise => exercise.id === settings?.exercise) || null; // group procedure
      this.slot = this.ac?.slots.find(slot => slot.id === settings?.slot) || null; // single procedure
      this.visible = this.exercise !== null || this.slot !== null;
      this.initFromStatus();
    });
  }

  ngOnDestroy(): void {
    if (this.acSubscription !== null) {
      this.acSubscription.unsubscribe();
    }
    if (this.settingsSubscription !== null) {
      this.settingsSubscription.unsubscribe();
    }
  }

  public initFromStatus(): void {
    if (this.ac && this.exercise) {
      const status = this.ac?.getExerciseStatus(this.exercise.id) || null;
      this.has_matrix = status?.has_matrix === true;
      this.automatic_start = status?.automatic_start === true;
    }
  }

  public hideSettings(): void {
    this.data.closeExerciseSettings();
  }

  public saveSettings(): void {
    if (this.exercise) {
      this.submitting = true;
      const payload = this.getPayload();
      this.api.setStatusSettings(this.exercise.id, payload).subscribe({
        next: (result: any) => {
          this.data.setNewAcStatus(result.data);
          this.hideSettings();
        },
        error: () => {
          this.ui.toastError(this.transloco.translate('partials.exercise_settings.messages.error_saving_settings'));
        }
      }).add(() => {
        this.submitting = false
      });
    }
  }

  private getPayload(): AcStatusSettingsExerciseType {
    const payload = this.groups.getPayload();
    payload.slots = this.slots.getPayload();
    payload.automatic_start = this.automatic_start;
    payload.has_matrix = this.has_matrix;
    return payload;
  }

  private initTabs(): void {
    if (this.ac?.isGroup()) {
      this.tabs = [
        { value: 'groups', label: this.transloco.translate('partials.exercise_settings.groups') },
        { value: 'slots', label: this.transloco.translate('partials.exercise_settings.timing_permissions') },
      ];
    } else {
      this.tabs = [
        { value: 'slots', label: this.transloco.translate('partials.exercise_settings.timing_permissions') },
      ];
    }
    this.activeTab = this.tabs[0];
  }

}
